import { useEffect, useState } from "react";
import type { AppRegistryEntry } from "@feedback-hub/contract/browser";

import { ApiError, getMetrics, listApps, type AppMetrics } from "../api.js";
import { formatDuration, formatRate } from "../format.js";

const MIN_N = 20;

export function Metrics() {
  const [apps, setApps] = useState<AppRegistryEntry[]>([]);
  const [appId, setAppId] = useState("");
  const [days, setDays] = useState(90);
  const [metrics, setMetrics] = useState<AppMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await listApps();
        if (cancelled) return;
        setApps(res.apps);
        if (!appId && res.apps[0]) setAppId(res.apps[0].appId);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Failed to load apps.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!appId) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getMetrics(appId, days);
        if (!cancelled) setMetrics(res.metrics);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Failed to load metrics.");
          setMetrics(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appId, days]);

  return (
    <div>
      <div className="filters panel">
        <div className="field">
          <label htmlFor="metrics-app">App</label>
          <select
            id="metrics-app"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
          >
            {apps.map((a) => (
              <option key={a.appId} value={a.appId}>
                {a.displayName}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="metrics-days">Days</label>
          <select
            id="metrics-days"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={30}>30</option>
            <option value={90}>90</option>
            <option value={180}>180</option>
            <option value={365}>365</option>
          </select>
        </div>
      </div>

      {loading && <p className="muted">Loading metrics…</p>}
      {error && <p className="error">{error}</p>}

      {metrics && !loading && (
        <>
          <div className="metrics-grid" style={{ marginBottom: "1rem" }}>
            <MetricCard label="Incidents" value={String(metrics.totals.incidents)} />
            <MetricCard label="Ratings" value={String(metrics.totals.ratings)} />
            <MetricCard label="Bugs" value={String(metrics.totals.bugs)} />
            <MetricCard label="With email" value={String(metrics.totals.withEmail)} />
            <MetricCard
              label="Loop closure"
              value={formatRate(metrics.loopClosureRate)}
            />
            <MetricCard
              label="Contact provided"
              value={formatRate(metrics.contactProvidedRate)}
            />
            <MetricCard label="Reopen rate" value={formatRate(metrics.reopenRate)} />
            <MetricCard label="Won't-fix ratio" value={formatRate(metrics.wontFixRatio)} />
            <MetricCard label="Spam ratio" value={formatRate(metrics.spamRatio)} />
            <MetricCard
              label="Triage p50 / p90"
              value={`${formatDuration(metrics.timeToTriageMs.p50)} / ${formatDuration(metrics.timeToTriageMs.p90)}`}
            />
            <MetricCard
              label="Resolution p50 / p90"
              value={`${formatDuration(metrics.timeToResolutionMs.p50)} / ${formatDuration(metrics.timeToResolutionMs.p90)}`}
            />
          </div>

          <div className="panel section" style={{ borderTop: 0, paddingTop: "1rem", marginTop: 0 }}>
            <h3>Satisfaction by surface</h3>
            <p className="muted" style={{ marginTop: 0 }}>
              Rates require at least {MIN_N} ratings; fewer renders as insufficient data.
            </p>
            {metrics.satisfactionBySurface.length === 0 ? (
              <p className="muted">No ratings in this window.</p>
            ) : (
              <div className="table-wrap">
                <table className="queue">
                  <thead>
                    <tr>
                      <th>Surface</th>
                      <th>Up</th>
                      <th>Down</th>
                      <th>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.satisfactionBySurface.map((row) => {
                      const n = row.up + row.down;
                      const display =
                        row.rate == null || n < MIN_N
                          ? "insufficient data"
                          : `${(row.rate * 100).toFixed(1)}%`;
                      return (
                        <tr key={row.surfaceId} style={{ cursor: "default" }}>
                          <td className="mono">{row.surfaceId}</td>
                          <td>{row.up}</td>
                          <td>{row.down}</td>
                          <td>{display}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="panel" style={{ marginTop: "1rem" }}>
            <h3>Top recurring (open)</h3>
            {metrics.topRecurring.length === 0 ? (
              <p className="muted">None.</p>
            ) : (
              <div className="table-wrap">
                <table className="queue">
                  <thead>
                    <tr>
                      <th>Ref</th>
                      <th>Occurrences</th>
                      <th>Status</th>
                      <th>Dedupe key</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.topRecurring.map((row) => (
                      <tr key={row.ref} style={{ cursor: "default" }}>
                        <td className="mono">{row.ref}</td>
                        <td>{row.occurrences}</td>
                        <td>{row.status}</td>
                        <td className="mono preview">{row.dedupeKey}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="panel" style={{ marginTop: "1rem" }}>
            <h3>Bugs by release</h3>
            {metrics.bugIncidenceByRelease.length === 0 ? (
              <p className="muted">None.</p>
            ) : (
              <div className="table-wrap">
                <table className="queue">
                  <thead>
                    <tr>
                      <th>App version</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.bugIncidenceByRelease.map((row) => (
                      <tr key={row.appVersion} style={{ cursor: "default" }}>
                        <td className="mono">{row.appVersion}</td>
                        <td>{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}
